import React from "react";
import { Container, Typography, Box, Divider, Alert, Paper, List, ListItem, ListItemIcon, ListItemText, Chip, Stack } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle, faCoins, faGavel, faLandmark, faBuilding, faChartLine, faArrowDown, faCalendarAlt } from "@fortawesome/free-solid-svg-icons";
import SectionTitle from "../../components/layouts/others/title-section.layout";

const POST20240117: React.FC = () => {
    return (
        <Container sx={{ mb: 5 }}>
            <SectionTitle title="ESPACE INFORMATIONS DES HABITANTS" />

            <Box mt={3} mb={1}>
                <Typography
                    variant="h6"
                    fontWeight={700}
                    align="center"
                    gutterBottom
                    color="primary"
                >
                    « Tout va très bien Madame la Marquise ! »
                </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, backgroundColor: "#f5f7fa" }}>
                <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                    <FontAwesomeIcon icon={faCoins} color="#1976d2" />
                    <Chip label="Situation financière" color="primary" size="small" />
                    <Typography variant="body2" color="text.secondary">
                        2018 : Commune saine, épargne excédentaire, trésorerie abondante.
                    </Typography>
                </Stack>
                <Typography variant="body2">
                    Pour éviter tout débat sur l'obligation SRU avant les élections, le Maire choisit la
                    discrétion. Dès <b>2021</b>, il agit en solitaire et sollicite l’<b>EPFIF</b>
                    (Établissement Public Foncier d’Île-de-France) sans concertation, mais fait machine arrière en raison de la mobilisation citoyenne lors du Conseil municipal du <b>18 novembre 2021</b>.
                </Typography>

                <Box mt={2} mb={2}>
                    <Alert icon={<FontAwesomeIcon icon={faArrowDown} />} severity="warning" sx={{ fontWeight: 500, background: "#fffbe6" }}>
                        Fin 2021 : Les recettes ne couvrent plus les dépenses.<br />
                        <FontAwesomeIcon icon={faChartLine} color="#d32f2f" /> Les réserves financières s’épuisent rapidement.
                        <br />
                        <Typography variant="caption">
                            (Voir notre lettre d'informations n°27 « Alerte sur nos finances » - 3 novembre 2023)
                        </Typography>
                    </Alert>
                </Box>

                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                    <FontAwesomeIcon icon={faLandmark} color="#388e3c" />
                    <Chip label="Obligation SRU" color="success" size="small" />
                </Stack>
                <Typography variant="body2">
                    <b>2023</b> : Marges de manœuvre perdues, finances exsangues malgré peu de logements locatifs sociaux (LLS) produits.<br />
                    Le préfet engage une <b>procédure de carence</b> :
                </Typography>
                <List dense>
                    <ListItem>
                        <ListItemIcon>
                            <FontAwesomeIcon icon={faGavel} color="#d32f2f" />
                        </ListItemIcon>
                        <ListItemText
                            primary="Objectifs triennaux imposés : 86 LLS à construire pour 2025."
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <FontAwesomeIcon icon={faBuilding} color="#1565c0" />
                        </ListItemIcon>
                        <ListItemText
                            primary="Zones ciblées : la Focel & la zone de la gare."
                        />
                    </ListItem>
                </List>

                <Box mt={2}>
                    <Typography variant="body2">
                        Situation paradoxale : sans moyens, aucun service ou projet sobre ne peut être lancé, tout doit passer par la commune... qui n'en a plus les capacités.
                    </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                    <FontAwesomeIcon icon={faCalendarAlt} />
                    <Chip label="2024 : Année décisive" color="warning" />
                </Stack>
                <Typography variant="body2">
                    Le Maire a une dernière année pour satisfaire le préfet. Sinon, celui-ci prendra la main
                    sur le droit de préemption pour lancer les constructions, risquant d’imposer des bâtiments denses (LLS).
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    La parcelle de 16 000 m² de la Focel permettra d’atteindre d’emblée cet objectif.
                </Typography>
            </Paper>

            <Box mt={3}>
                <Alert icon={<FontAwesomeIcon icon={faExclamationTriangle} />} severity="info" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                        Questions et constats
                    </Typography>
                    <ul style={{ marginTop: 4, marginBottom: 0, paddingLeft: 20 }}>
                        <li>
                            <b>Aucune réflexion</b> menée depuis 2018 pour réaliser du LLS par des constructions sobres et/ou la rénovation.
                        </li>
                        <li>
                            La municipalité n'a plus les moyens d'envisager ces solutions, les finances ayant été dégradées.
                        </li>
                        <li>
                            <b>Attention</b> : aucune parcelle n’est totalement protégée par le PLU ou les servitudes d’urbanisme. Le préfet peut adapter le PLU pour implémenter l’obligation SRU sur tout le foncier communal.
                        </li>
                    </ul>
                </Alert>
                <Alert icon={<FontAwesomeIcon icon={faExclamationTriangle} />} severity="error">
                    Si les 86 LLS ne sont pas créés en 2025, recours à l’EPFIF imposé par le préfet.
                    <br />
                    <i>Est-ce le choix du Maire, vu cette déclaration recueillie en 2023&nbsp;: </i>
                    <br /><b>
                    « ceux qui m’ont empêché de signer avec l’EPFIF s’en mordront les doigts »
                </b>
                </Alert>
            </Box>

        </Container>
    );
};

export default POST20240117;